import { useState } from "react";
import type { LoggedMeal } from "../lib/types";
import type { Goals } from "../lib/goals";
import { localDateOf } from "../lib/storage";

interface Props {
  meals: LoggedMeal[];
  goals: Goals;
}

interface DayBar {
  iso: string;
  label: string;
  longLabel: string;
  kcal: number;
  isToday: boolean;
}

export default function WeeklyChart({ meals, goals }: Props) {
  const days = lastNDays(7);
  const bars: DayBar[] = days.map((d, i) => {
    const iso = isoDate(d);
    const kcal = meals
      .filter((m) => localDateOf(m.logged_at) === iso)
      .reduce((s, m) => s + m.estimate.totals.calories, 0);
    return {
      iso,
      label: d.toLocaleDateString(undefined, { weekday: "narrow" }),
      longLabel: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
      kcal,
      isToday: i === days.length - 1
    };
  });

  const maxKcal = Math.max(goals.calories, ...bars.map((b) => b.kcal), 1);
  const avg = bars.reduce((s, b) => s + b.kcal, 0) / bars.length;
  const goalLineY = 100 - (goals.calories / maxKcal) * 100;

  // Selected bar shows a tooltip below the chart with kcal vs goal. Tap to
  // toggle (UI audit High #11). Touch-friendly: no hover, no flicker.
  const [selectedIso, setSelectedIso] = useState<string | null>(null);
  const selected = bars.find((b) => b.iso === selectedIso) ?? null;

  return (
    <div className="mx-4 p-4 rounded-2xl bg-slate-900 border border-slate-800">
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-sm text-slate-300 font-medium">Last 7 days</span>
        <span className="text-xs text-slate-400">
          avg <span className="text-slate-200">{Math.round(avg)}</span> kcal/day
        </span>
      </div>

      <div className="relative h-32">
        {/* goal line */}
        <div
          className="absolute inset-x-0 border-t border-dashed border-emerald-500/40 pointer-events-none"
          style={{ top: `${goalLineY}%` }}
        >
          <span className="absolute -top-3 right-0 text-[10px] text-emerald-400 bg-slate-900 px-1">
            goal
          </span>
        </div>

        {/* bars */}
        <div className="absolute inset-0 flex items-end justify-between gap-1.5">
          {bars.map((b) => {
            const h = (b.kcal / maxKcal) * 100;
            const overGoal = b.kcal > goals.calories;
            const isSelected = b.iso === selectedIso;
            return (
              <button
                type="button"
                key={b.iso}
                onClick={() => setSelectedIso(isSelected ? null : b.iso)}
                aria-label={`${b.longLabel}: ${Math.round(b.kcal)} kcal of ${goals.calories} goal`}
                aria-pressed={isSelected}
                className={`flex-1 flex flex-col items-center justify-end h-full rounded ${
                  isSelected ? "ring-1 ring-slate-500" : ""
                }`}
              >
                <div className="w-full relative" style={{ height: "100%" }}>
                  <div
                    className={`absolute bottom-0 inset-x-0 rounded-t transition-all ${
                      b.isToday
                        ? overGoal
                          ? "bg-rose-400"
                          : "bg-emerald-400"
                        : overGoal
                        ? "bg-rose-500/40"
                        : "bg-emerald-400/20"
                    }`}
                    style={{ height: `${h}%`, minHeight: b.kcal > 0 ? "2px" : "0" }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between gap-1.5 mt-2">
        {bars.map((b) => (
          <div
            key={b.iso}
            className={`flex-1 text-center text-[10px] ${
              b.isToday ? "text-emerald-400 font-semibold" : "text-slate-400"
            }`}
          >
            {b.label}
          </div>
        ))}
      </div>

      {/* Tap-tooltip — replaces the per-bar floating tooltip with a stable
          row underneath. Less fiddly on touch and never collides with the
          adjacent bars. */}
      {selected && (
        <div className="mt-3 p-2.5 rounded-lg bg-slate-800/60 border border-slate-700 flex items-baseline justify-between">
          <div>
            <div className="text-xs text-slate-300">{selected.longLabel}</div>
            <div className="text-base font-semibold text-slate-100 tabular-nums">
              {Math.round(selected.kcal)} kcal
            </div>
          </div>
          <div className="text-xs text-slate-400 text-right">
            {selected.kcal === 0
              ? "nothing logged"
              : selected.kcal > goals.calories
              ? `+${Math.round(selected.kcal - goals.calories)} over goal`
              : `${Math.round(goals.calories - selected.kcal)} under goal`}
          </div>
        </div>
      )}
    </div>
  );
}

function lastNDays(n: number): Date[] {
  const out: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    out.push(d);
  }
  return out;
}

function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
